import {AfterViewChecked, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {MatTabChangeEvent, MatTabGroup} from '@angular/material/tabs';
import {finalize} from 'rxjs/operators';
import {forkJoin, Subject} from 'rxjs';

import {Individu, IndividuService, PubSubService} from '@Common/core/services';
import {SubSink} from '@Common/core/utils/subsink';
import {CommentService} from '@Common/widgets/general/comments/service/comment.service';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';

/*import {CommentModel} from '@Common/widgets/general/comments/interfaces/comment.model';
import {CommentTypeEnum} from '@Common/widgets/general/comments/interfaces/comment-type.enum';
import {AttachmentModel} from '@Common/widgets/general/attachments/interfaces/attachment.model';
import {ApplicationEnum} from '@Common/widgets/general/attachments/interfaces/application.enum';
import {CommentComponentCallback} from '@Common/widgets/general/comments/comment/comment-callback.interface';
import {CommentTriggerModel} from '@Common/widgets/general/comments/service/comment-trigger.model';*/

import {HtmlHelperService} from '@Services/core/utils/html-helper.service';
import {ApplicationEnum} from '@Common/widgets/general/attachments/interfaces/application.enum';
import {CommentComponentCallback} from '@Common/widgets/general/comments/comment/comment-callback.interface';
import {CommentModel} from '@Common/widgets/general/comments/interfaces/comment.model';
import {CommentTypeEnum} from '@Common/widgets/general/comments/interfaces/comment-type.enum';
import {CommentTriggerModel} from '@Common/widgets/general/comments/service/comment-trigger.model';
import {AttachmentModel} from '@Common/widgets/general/attachments/interfaces/attachment.model';


/**
 * Composant d'écriture de nouveau commentaire
 * format : HTML via QuillEditor
 * {
 *   "content": "mon commentaire",
 *   "type": 1,
 *   "commentator": "oduval02",
 *   "object_id": 50, -- pk objet rattaché
 *   "content_type": 272 -- type de l'objet, cf. django_content_type
 * }
 * Côté backend, l'objet rattaché (content_object) est retrouvé par la combinaison de object_id & content_type
 * Si l'envoi mail est activé (sendMail) :
 *   - le modèle par défaut est utilisé via l'API CommentService.sendmail()
 *   pour surcharger l'objet du mail par défaut et/ou mettre un lien, injecter linkToOpenOrTitle: CommentComponentCallback,
 *   le composant père doit implémenté cette interface
 *   - l'objet du mail par défaut peut être surchargé via getTitle()
 *   - un lien vers les messages peut être mis via getLink()
 * Pour un envoi de mail plus personnalisé, mettre sendMail à False et utiliser l'Output commentSended
 * pour faire le traitement front et back côté application cliente de ce composant
 */
@Component({
  selector: 'p8-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss']
})
export class CommentComponent implements OnInit, AfterViewChecked, OnDestroy {
  /**
   * L'objet concerné par le commentaire, son pk id : par exemple ResponseDemat
   */
  @Input() object_id: number;
  /**
   * Le content_type de l'objet concerné, cf. django_content_type
   */
  @Input() content_type: number;
  /**
   * Pour les fichiers, l'application concernée, le répertoire de stockage des fichiers, par ex. : demat
   */
  @Input() application: ApplicationEnum = ApplicationEnum.default;

  @Input() labelButtonSend = 'Envoyer';
  @Input() labelButtonCancel = 'Annuler';
  @Input() labelComment = 'Commentaire';
  @Input() labelPublic = 'Message public';
  @Input() labelPrivate = 'Message interne';

  @Input() alignButtonsPublicPrivate: 'start' | 'end' = 'end';
  @Input() alignButtonsSendCancel: 'start' | 'end' = 'end';

  @Input() hasCancelButton = false;
  @Input() hasFilesToUpload = false;

  @Input() typeFileButton: 'icon' | 'label' = 'icon';
  @Input() alignFilesUpload: 'start' | 'end' = 'end';
  @Input() acceptFiles = '.pdf,.txt,.jpg,.png,.gif,.ods,.xls,.xlsx,.odt,.doc,.docx,.ppt';
  @Input() maxFileSize = 5000000;
  @Input() maxFiles = 1;
  @Input() labelJoinFiles = `Joindre le${this.maxFiles > 1 ? 's' : ''} fichier${this.maxFiles > 1 ? 's' : ''}`;
  @Input() labelFileMaxsize = `(limite à ${this.maxFileSize / 1000000} Mo maximum chacun)`;
  @Input() colorButton: 'accent' | 'primary' | 'warn' | undefined = undefined;
  /**
   * A-t-il des messages privés ?
   */
  @Input() hasPrivate = false;
  /**
   * Doit-on envoyer un mail (modèle par défaut) du commentaire ?
   * l'émetteur correspond au connecté (commentator)
   */
  @Input() sendMail = false;
  /**
   * Les emails destinataires du mail selon public / privé
   * pris en compte si sendMail = true
   */
  @Input() sendTosPrivate: string[] = []; // à notifier quand message privé
  @Input() sendTosPublic: string[] = []; // à notifier quand message public
  /**
   * La fonction qui renverra le chemin de l'URL à ouvrir dans la notification le cas échéant ou le titre du mail envoyé
   * Le chemin doit être en absolu, sans le domaine, par ex. /demat/RESPONSEID/messages
   * pris en compte si sendMail = true
   * @private
   */
  private _functionLinkMessage: CommentComponentCallback;
  @Input() set linkToOpenOrTitle(component: CommentComponentCallback) {
    this._functionLinkMessage = component;
  }
  /**
   * Emission lors d'un nouveau commentaire
   */
  @Output() commentSended: EventEmitter<CommentModel> = new EventEmitter<CommentModel>();
  /**
   * Commentaire
   */
  IDX_PUBLIC = 0;
  IDX_PRIVATE = 1;
  selectedTab = this.IDX_PUBLIC;
  selectedType: CommentTypeEnum = CommentTypeEnum.public;
  commentTypeEnum = CommentTypeEnum;
  sendingComment = false;
  content = '';
  commentFormGroup: UntypedFormGroup;
  individuConnected: Individu;
  comment = <CommentModel>{ id: 0 };
  /**
   * Configuration Quill
   */
  formats = [
    'bold',
    'italic',
    'list',
    'indent',
  ];

  modules = {
    toolbar: [
      ['bold', 'italic'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
    ]
  };
  /**
   * Fichiers
   */
  uploaderConnector: Subject<any> = new Subject();
  uploading$: Subject<boolean> = new Subject<boolean>();
  hasAttachments = false;
  uploading = false;
  object_id_comment: number;
  content_type_comment: number;
  subSink = new SubSink();

  @ViewChild(MatTabGroup, { static: false }) matTabGroup: MatTabGroup;
  constructor(private formBuilder: UntypedFormBuilder,
              private pubSubSvc: PubSubService,
              private snackSvc: SnackBarToastyService,
              private individuSvc: IndividuService,
              private commentSvc: CommentService) { }

  ngOnInit(): void {
    this.subSink.sink = this.individuSvc.individu$
      .subscribe(_individu => this.individuConnected = _individu);
    this._razComment();
  }
  _commentSended(_comment) {
    this.hasAttachments = false;
    this.commentSended.emit(_comment);
    this.snackSvc.toasty('Commentaire', 'Commentaire bien envoyé', typeToast.success);
    this._triggerNewComment(_comment);
    this._razComment();
  }

  /**
   * On exclut la personne qui commente de la liste des emails destinataires
   * @param emails
   */
  private _getMails(emails: string[]): string[] {
    return emails.filter(_email => _email !== this.comment.commentator_obj?.email_pro);
  }
  private _notifyDefault(tos: string[], title: string = null, link: string = null, linkLabel: string = null) {
    this.comment.content_plain = HtmlHelperService.plainText(this.comment.content, true);
    this.subSink.sink = this.commentSvc
      .sendmail(this.comment, tos, title, link, linkLabel)
      .subscribe(_result => this.snackSvc.toasty('Message', 'Courriel bien envoyé', typeToast.success));
  }
  private _getTos() {
    if (this.comment.type === CommentTypeEnum.public) {
      return this._getMails(this.sendTosPublic);
    }
    if (this.comment.type === CommentTypeEnum.private) {
      return this._getMails(this.sendTosPrivate);
    }
    return [];
  }
  private _sendmail() {
    if (this.sendMail) {
      const tos = this._getTos();
      if (tos.length > 0) {
        if (this._functionLinkMessage) {
          const title$ = this._functionLinkMessage.getTitle(this.comment, this.object_id);
          const link$ = this._functionLinkMessage.getLink(this.comment, this.object_id);
          const linkLabel$ = this._functionLinkMessage.getLinkLabel(this.comment, this.object_id);
          forkJoin([title$, link$, linkLabel$]).subscribe((_results) =>
            this._notifyDefault(tos, _results[0], _results[1], _results[2]));
        } else {
          this._notifyDefault(tos);
        }
      }
    }
  }
  private _triggerNewComment(_comment) {
    const commentTrigger = <CommentTriggerModel> {
      comment: _comment,
      action: 'add'
    };
    this.commentSvc.triggerComment(commentTrigger);
    this._sendmail();
  }
  sendComment(): void {
    this.sendingComment = true;
    this._formToModel();
    this.subSink.sink = this.commentSvc
      .updateOrcreate(this.comment)
      .pipe(finalize(() => this.sendingComment = false))
      .subscribe((_comment: CommentModel) => {
        this.comment = {..._comment};
        if (this.hasFilesToUpload && this.hasAttachments) {
          this._startUpload(_comment);
        } else {
          this._commentSended(_comment);
        }
      });
  }
  /**
   * Couleur de fond quill editor
   */
  getClass() {
    if (this.selectedType === CommentTypeEnum.private) {
      return 'private_container';
    }
    return '';
  }
  onChangedType(event: MatTabChangeEvent) {
    if (event.index === this.IDX_PUBLIC) {
      this.selectedType = CommentTypeEnum.public;
    }
    if (event.index === this.IDX_PRIVATE) {
      this.selectedType = CommentTypeEnum.private;
    }
  }
  /**
   * Détection s'il y a des fichiers à téléverser après la sauvegarde du commentaire
   * @param attachments
   */
  hasAttachmentsToUpload(attachments: AttachmentModel[]) {
    this.hasAttachments = attachments.length > 0;
  }
  /**
   * Fin téléchargement fichier
   * @param attachments
   */
  handleFilesUploaded(attachments: AttachmentModel[]): void {
    this.hasAttachments = false;
    this.uploading = false;
    this.pubSubSvc.publish('loading', false);
    this._commentSended(this.comment);
  }
  private _razComment() {
    this.content = '';
    this.comment = <CommentModel>{ id: 0 };
  }
  /**
   * Démarrage upload fichier le cas échéant
   * @param {CommentModel} comment
   */
  private _startUpload(comment: CommentModel) {
    if (comment) {
      comment.object_id = comment.id;
      comment.content_type = comment.content_type_comment;
      this.pubSubSvc.publish('loading', true);
      this.uploading = true;
      this.uploaderConnector.next(comment);
    }
  }
  private _formToModel() {
    // commentaire
    this.comment.content = this.content;
    this.comment.type = this.selectedType;
    // méta
    this.comment.commentator = this.individuConnected.uid;
    this.comment.object_id = this.object_id;
    this.comment.content_type = this.content_type;
  }
  /**
   * Force le surlignage de l'onglet la 1ère fois
   */
  realignInkBar() {
    if (this.matTabGroup) {
      this.matTabGroup.realignInkBar();
    }
  }
  ngAfterViewChecked(): void {
    this.realignInkBar();
  }
  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }
}
